import { useState } from 'react';
import { colorOptions, relationshipConfig, interestOptions, genderOptions } from './relationshipConfig';

import { Label } from '@/components/ui/label';
import { Calendar, Plus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
export default function EditRecipientModal({ 
  isOpen, 
  recipient, 
  formData,
  onClose, 
  onFormDataChange,
  onSave, 
  darkMode 
}) {
  const [customInterest, setCustomInterest] = useState('');
  const [newOccasion, setNewOccasion] = useState({ name: '', date: '' });

  const toggleInterest = (interest) => {
    const currentInterests = formData.interests || [];
    if (currentInterests.includes(interest)) {
      onFormDataChange({
        ...formData,
        interests: currentInterests.filter(i => i !== interest)
      });
    } else {
      onFormDataChange({
        ...formData,
        interests: [...currentInterests, interest]
      });
    }
  };

  const addCustomInterest = () => {
    if (customInterest.trim() && !(formData.interests || []).includes(customInterest.trim())) {
      onFormDataChange({
        ...formData,
        interests: [...(formData.interests || []), customInterest.trim()]
      });
      setCustomInterest('');
    }
  };

  const removeInterest = (interest) => {
    onFormDataChange({
      ...formData,
      interests: (formData.interests || []).filter(i => i !== interest)
    });
  };

  const addOccasion = () => {
    if (newOccasion.name.trim() && newOccasion.date) {
      onFormDataChange({
        ...formData,
        occasions: [...(formData.occasions || []), { ...newOccasion }]
      });
      setNewOccasion({ name: '', date: '' });
    }
  };

  const removeOccasion = (index) => {
    onFormDataChange({
      ...formData,
      occasions: (formData.occasions || []).filter((_, i) => i !== index)
    });
  };

  // Get custom interests (ones not in the predefined list)
  const customInterests = (formData.interests || []).filter(i => !interestOptions.includes(i));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" style={{ backgroundColor: darkMode?.isDark ? '#2D4354' : 'white' }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Playfair Display, serif', color: darkMode?.isDark ? '#FED7A5' : '#20212B', fontSize: '24px' }}>
            Edit {recipient?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 mt-4">
          {/* Name */}
          <div>
            <Label style={{ color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>Name</Label>
            <Input
              value={formData.name || ''}
              onChange={(e) => onFormDataChange({ ...formData, name: e.target.value })}
              className="mt-1"
              style={{ backgroundColor: darkMode?.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white', color: darkMode?.isDark ? '#FED7A5' : '#1F2937', border: `1px solid ${darkMode?.isDark ? '#FED7A5' : '#E5E5E5'}` }}
            />
          </div>

          {/* Relationship */}
          <div>
            <Label style={{ color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>Relationship</Label>
            <Select
              value={formData.relationship || ''}
              onValueChange={(value) => onFormDataChange({ ...formData, relationship: value })}
            >
              <SelectTrigger className="mt-1" style={{ backgroundColor: darkMode?.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white', color: darkMode?.isDark ? '#FED7A5' : '#1F2937' }}>
                <SelectValue placeholder="Select relationship" />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(relationshipConfig).map(rel => (
                  <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Gender — steers audience-aware recommendations */}
          <div>
            <Label style={{ color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>Shopping for</Label>
            <div className="flex gap-2 mt-1">
              {genderOptions.map(g => (
                <button
                  key={g.label}
                  type="button"
                  data-testid={`edit-gender-${g.label.toLowerCase()}`}
                  onClick={() => onFormDataChange({ ...formData, gender: g.value })}
                  className="px-4 py-1.5 rounded-full text-sm font-medium transition-all"
                  style={{
                    background: (formData.gender || '') === g.value ? '#9E6752' : (darkMode?.isDark ? 'rgba(255, 255, 255, 0.1)' : '#F5F5F5'),
                    color: (formData.gender || '') === g.value ? 'white' : (darkMode?.isDark ? '#FED7A5' : '#534145'),
                    border: `1px solid ${(formData.gender || '') === g.value ? '#9E6752' : (darkMode?.isDark ? '#FED7A5' : '#E5E5E5')}`
                  }}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          {/* Gift Preferences, personality + budget (moved here from the
              old separate Gift Preferences modal so everything is editable
              in one place) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label style={{ color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>Personality Type</Label>
              <Select
                value={formData.personality || ''}
                onValueChange={(value) => onFormDataChange({ ...formData, personality: value })}
              >
                <SelectTrigger className="mt-1" data-testid="edit-recipient-personality" style={{ backgroundColor: darkMode?.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white', color: darkMode?.isDark ? '#FED7A5' : '#1F2937' }}>
                  <SelectValue placeholder="Select personality" />
                </SelectTrigger>
                <SelectContent>
                  {['Creative', 'Introverted', 'Outdoorsy', 'Homebody', 'Sentimental', 'Practical', 'Stylish', 'Tech-y', 'Funny/Playful'].map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label style={{ color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>Budget Range</Label>
              <Select
                value={formData.budget_range || ''}
                onValueChange={(value) => onFormDataChange({ ...formData, budget_range: value })}
              >
                <SelectTrigger className="mt-1" data-testid="edit-recipient-budget" style={{ backgroundColor: darkMode?.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white', color: darkMode?.isDark ? '#FED7A5' : '#1F2937' }}>
                  <SelectValue placeholder="Select budget" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Under $25">Under $25</SelectItem>
                  <SelectItem value="$25-$50">$25 - $50</SelectItem>
                  <SelectItem value="$50-$100">$50 - $100</SelectItem>
                  <SelectItem value="$100-$200">$100 - $200</SelectItem>
                  <SelectItem value="$200+">$200+</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Interests - Selectable Pills */}
          <div>
            <Label style={{ color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>Interests</Label>
            <p className="text-xs mt-1 mb-3" style={{ color: darkMode?.isDark ? 'rgba(254, 215, 165, 0.7)' : '#73766A' }}>
              Select interests or add your own
            </p>
            
            {/* Predefined Interest Pills */}
            <div className="flex flex-wrap gap-2 mb-3">
              {interestOptions.map(interest => {
                const isSelected = (formData.interests || []).includes(interest);
                return (
                  <button
                    key={interest}
                    type="button"
                    onClick={() => toggleInterest(interest)}
                    className="px-3 py-1.5 rounded-full text-sm font-medium transition-all hover:scale-105"
                    style={{
                      background: isSelected 
                        ? '#9E6752' 
                        : (darkMode?.isDark ? 'rgba(255, 255, 255, 0.1)' : '#F5F5F5'),
                      color: isSelected 
                        ? 'white' 
                        : (darkMode?.isDark ? '#FED7A5' : '#534145'),
                      border: `1px solid ${isSelected ? '#9E6752' : (darkMode?.isDark ? 'rgba(254, 215, 165, 0.3)' : '#E5E5E5')}`
                    }}
                  >
                    {interest}
                  </button>
                );
              })}
            </div>

            {/* Custom Interests Display */}
            {customInterests.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {customInterests.map(interest => (
                  <div
                    key={interest}
                    className="px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5"
                    style={{
                      background: '#9E6752',
                      color: 'white'
                    }}
                  >
                    {interest}
                    <button
                      type="button"
                      onClick={() => removeInterest(interest)}
                      className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add Custom Interest */}
            <div className="flex gap-2">
              <Input
                value={customInterest}
                onChange={(e) => setCustomInterest(e.target.value)}
                placeholder="Add custom interest..."
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomInterest())}
                style={{ 
                  backgroundColor: darkMode?.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white', 
                  color: darkMode?.isDark ? '#FED7A5' : '#1F2937', 
                  border: `1px solid ${darkMode?.isDark ? 'rgba(254, 215, 165, 0.3)' : '#E5E5E5'}`,
                  flex: 1
                }}
              />
              <Button
                type="button"
                onClick={addCustomInterest}
                disabled={!customInterest.trim()}
                className="px-3"
                style={{ 
                  background: darkMode?.isDark ? 'rgba(254, 215, 165, 0.2)' : '#F5F5F5',
                  color: darkMode?.isDark ? '#FED7A5' : '#534145'
                }}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Occasions */}
          <div>
            <Label style={{ color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>
              <Calendar className="w-4 h-4 inline mr-2" />
              Occasions
            </Label>
            <p className="text-xs mt-1 mb-3" style={{ color: darkMode?.isDark ? 'rgba(254, 215, 165, 0.7)' : '#73766A' }}>
              Add birthdays, anniversaries, and other special dates
            </p>

            {/* Existing Occasions */}
            {(formData.occasions || []).length > 0 && (
              <div className="space-y-2 mb-3">
                {(formData.occasions || []).map((occasion, index) => (
                  <div
                    key={`occasion-${index}-${occasion.name}`}
                    className="flex items-center justify-between p-3 rounded-lg"
                    style={{
                      background: darkMode?.isDark ? 'rgba(255, 255, 255, 0.1)' : '#F5F5F5',
                      border: `1px solid ${darkMode?.isDark ? 'rgba(254, 215, 165, 0.3)' : '#E5E5E5'}`
                    }}
                  >
                    <div className="flex-1">
                      <p className="font-medium" style={{ color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>
                        {occasion.name}
                      </p>
                      <p className="text-sm" style={{ color: darkMode?.isDark ? 'rgba(254, 215, 165, 0.7)' : '#73766A' }}>
                        {new Date(occasion.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeOccasion(index)}
                      className="hover:bg-red-500/20 rounded-full p-2 transition-colors"
                      style={{ color: '#EF4444' }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Occasion */}
            <div className="space-y-2">
              <Input
                value={newOccasion.name}
                onChange={(e) => setNewOccasion({ ...newOccasion, name: e.target.value })}
                placeholder="Occasion name (e.g., Birthday, Anniversary)"
                style={{ 
                  backgroundColor: darkMode?.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white', 
                  color: darkMode?.isDark ? '#FED7A5' : '#1F2937', 
                  border: `1px solid ${darkMode?.isDark ? 'rgba(254, 215, 165, 0.3)' : '#E5E5E5'}`
                }}
              />
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={newOccasion.date}
                  onChange={(e) => setNewOccasion({ ...newOccasion, date: e.target.value })}
                  style={{ 
                    backgroundColor: darkMode?.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white', 
                    color: darkMode?.isDark ? '#FED7A5' : '#1F2937', 
                    border: `1px solid ${darkMode?.isDark ? 'rgba(254, 215, 165, 0.3)' : '#E5E5E5'}`,
                    flex: 1
                  }}
                />
                <Button
                  type="button"
                  onClick={addOccasion}
                  disabled={!newOccasion.name.trim() || !newOccasion.date}
                  className="px-4"
                  style={{ 
                    background: (newOccasion.name.trim() && newOccasion.date) ? '#9E6752' : (darkMode?.isDark ? 'rgba(254, 215, 165, 0.2)' : '#F5F5F5'),
                    color: (newOccasion.name.trim() && newOccasion.date) ? 'white' : (darkMode?.isDark ? '#FED7A5' : '#534145')
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label style={{ color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>Notes</Label>
            <Textarea
              value={formData.notes || ''}
              onChange={(e) => onFormDataChange({ ...formData, notes: e.target.value })}
              placeholder="Favorite colors, sizes, things to avoid..."
              rows={3}
              className="mt-1"
              style={{ backgroundColor: darkMode?.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white', color: darkMode?.isDark ? '#FED7A5' : '#1F2937', border: `1px solid ${darkMode?.isDark ? '#FED7A5' : '#E5E5E5'}` }}
            />
          </div>

          {/* Profile Color */}
          <div>
            <Label style={{ color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>Profile Color</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {colorOptions.map(colorOption => (
                <button
                  key={colorOption}
                  type="button"
                  onClick={() => onFormDataChange({ ...formData, color: colorOption })}
                  className="w-9 h-9 rounded-full transition-all hover:scale-110"
                  style={{
                    backgroundColor: colorOption,
                    border: formData.color === colorOption ? '3px solid #FED7A5' : '2px solid transparent',
                    boxShadow: formData.color === colorOption ? '0 0 0 2px rgba(254, 215, 165, 0.4)' : 'none'
                  }}
                  title={colorOption}
                />
              ))}
            </div>
          </div>

          {/* Save Button */}
          <Button
            onClick={onSave}
            className="w-full transition-all hover:scale-[1.02]"
            style={{ background: '#9E6752', color: 'white', borderRadius: '10px', padding: '12px' }}
          >
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
